/**
 * Hook that builds the side-tab list for the external single-order layout
 * and auto-navigates to the first tab exactly once per order load.
 *
 * Replaces the prior implementation that called `navigate()` and
 * `setState()` from inside a `useMemo` (an impure-memo anti-pattern).
 *
 * @module singleOrderForExternal/hooks/useExternalOrderTabs
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthOnFile } from "../../../data-model/medicalPaOrdersModel";
import { useFacilityCategory } from "../../../hooks/useOncoemrTasksEnabled";
import { determineFinalTabsListToBeShown } from "../utils/tabSwitchElementsGenerator";

/** Shape passed in by the data accumulator wrapper. */
interface AuthOnFileEnvelope {
  auth_on_file?: AuthOnFile;
}

/**
 * @param authOnFileDetails - Auth-on-file envelope from the data accumulator
 * @returns Memoized list of tab descriptors (`id`, `label`)
 */
export const useExternalOrderTabs = (
  authOnFileDetails: AuthOnFileEnvelope | null | undefined,
) => {
  const navigate = useNavigate();
  const [isAutoNavigated, setIsAutoNavigated] = useState(false);
  const facilityCategory = useFacilityCategory();

  const tabSwitchElements = useMemo(() => {
    if (!authOnFileDetails) return [];
    return determineFinalTabsListToBeShown(
      authOnFileDetails.auth_on_file as AuthOnFile,
      facilityCategory,
    );
  }, [authOnFileDetails, facilityCategory]);

  useEffect(() => {
    if (!isAutoNavigated && tabSwitchElements.length > 0) {
      navigate(tabSwitchElements[0].id);
      setIsAutoNavigated(true);
    }
  }, [tabSwitchElements, isAutoNavigated, navigate]);

  return tabSwitchElements;
};
